/**
 * ColoresEstados
 * ----------------
 * Componente React encargado de renderizar el estado de un registro
 * utilizando etiquetas (Tag) de PrimeReact con colores personalizados.
 *
 * Se usa principalmente dentro de tablas (ej: AgGrid, DataTable),
 * donde el estado puede venir directamente o dentro de rowData.data.
 *
 * Autor: Mauricio B. Zavala
 * Última modificación: —
 */
import { color4, color5, color6, gray700, error, despachado, firmaDespachado } from '../../../PPF-ERP/src/Styles/Colors'
import { Checkbox, Tag } from 'primereact';

/**
 * @param {Object} props
 * @param {Object} props.rowData - Datos de la fila (AgGrid o DataTable)
 * @param {String} props.tipo - Tipo de renderizado (por defecto 'estado')
 * @param {Object} props.funciones - Funciones auxiliares (permisos u acciones futuras)
 */
// Cambiamos a desestructuración { rowData } para asegurar que React reciba bien la prop
export const ColoresEstados = ({ rowData, tipo = 'estado', funciones = {} }) => {
    /**
  * Normalización de datos:
  * - AgGrid suele enviar los datos dentro de rowData.data
  * - Otros componentes envían el objeto directamente
  * Esta línea asegura compatibilidad con ambos casos.
  */
    // Si rowData es null, intentamos ver si viene dentro de una prop 'data' (común en AgGrid)
    const data = rowData?.data || rowData;
    /**
  * Validación temprana:
  * Si no existen datos o no existe StatusName,
  * no se renderiza nada para evitar errores en React.
  */
    if (!data || !data.StatusName) return null;
    /**
   * Caso especial:
   * Cuando el estado contiene simultáneamente
   * "En Firma" y "Despachado"
   *
   * Ejemplo:
   *  - "En Firma - Despachado"
   *
   * Se renderiza con un color verde específico.
   */
    if (data.StatusName.includes('En Firma') && data.StatusName.includes('Despachado')) {
        return <Tag className='col-12' value={data.StatusName} style={{ background: '#679F37' }} />;
    }
    /**
 * Renderizado principal por tipo
 * Actualmente solo se maneja el tipo 'estado'
 */
    if (tipo === 'estado') {

        switch (data.StatusName) {
            /**
     * Estado: Pendiente Autorizar
     * ID de referencia: 3569
     */
            case 'Pendiente Autorizar': // ID 3569
                return <Tag className='col-12' value={data.StatusName} style={{ background: color5 }} />;
            /**
  * Estado: Pendiente
  * Usa el severity estándar de PrimeReact
  */
            case 'Pendiente':
                return <Tag severity='success' className='col-12' value={data.StatusName} />;
            /**
 * Estado: En Firma
 */
            case 'En Firma':
                return <Tag severity='info' className='col-12' value={data.StatusName} />;
            /**
   * Estado: Despachado
   */
            case 'Despachado':
                return <Tag severity='info' className='col-12' value={data.StatusName} style={{ background: despachado }} />;
            /**
  * Estado: Pendiente Despacho
  */
            case 'Pendiente Despacho':
                return <Tag severity='info' className='col-12' value={data.StatusName} style={{ background: color4 }} />;
            /**
   * Estado: Pendiente MASS
   */
            case 'Pendiente MASS':
                return <Tag severity='info' className='col-12' value={data.StatusName} style={{ background: color6 }} />;
            /**
* Estado: Cancelado
*/
            case 'Cancelado':
                return <Tag severity='info' className='col-12' value={data.StatusName} style={{ background: error }} />;
            /**
* Estado: Borrado
*/
            case 'Borrado':
                return <Tag severity='info' className='col-12' value={data.StatusName} style={{ background: gray700 }} />;
            /**
   * Estado no contemplado:
   * Se muestra el texto sin formato para evitar errores
   */
            default:
                return <div>{data.StatusName}</div>;
        }
    }
    /**
  * Retorno por defecto:
  * - Cuando el tipo no es 'estado'
  * - Cuando se agreguen permisos u otros comportamientos
  */
    // ... resto del código de permisos
    return null;
};  